// Matrix view (`/{boardId}`). Renders the 2×2 grid, task rows with
// editing-ring affordances for remote clients, the toolbar (status +
// avatar roster + settings), and mounts the cursor overlay.

import type {Quadrant} from "../generated/Quadrant";
import type {Task} from "../generated/Task";
import {local, localApply, priorityNumber, tasksInQuadrant} from "../state";
import {generateKeyBetween} from "../shared/ranking";
import {todayLocalIsoDate} from "../shared/today";
import {appendTag, matches, parseQuery, TAG_RE} from "../search";
import {copyMarkdownToClipboard} from "./export";
import {mountCursorLayer, renderRoster, updateCursors} from "./presence";
import {renderFilterBar, showFilterBar, toggleFilterBar} from "./search";
import {openSettingsModal} from "./settings";
import {openTaskModal} from "./modal";
import {effectiveTheme, toggleTheme} from "./theme";
import {isDragSuppressingClicks} from "../input/dragdrop";
import {boardShareUrl, isTauri, mcpUrl} from "../config/backend";
import {openBoardsMenu} from "./boards";

/** Move a task into `targetQuadrant`. If `beforeTaskId` is null the task
 *  is appended to the end of the quadrant's list; otherwise it is
 *  inserted immediately before the named sibling. Computes a fresh
 *  fractional rank and emits a `move` op. No-op when the resulting slot
 *  is the source's current slot.
 *
 *  Shared with the pointer-based DnD controller in `input/dragdrop.ts`.
 */
export function applyMove(
    draggedId: string,
    targetQuadrant: Quadrant,
    beforeTaskId: string | null,
): void {
    if (draggedId === beforeTaskId) return;
    const cur = local.state.tasks[draggedId];
    if (!cur) return;

    const siblings = tasksInQuadrant(targetQuadrant).filter(
        ([id]) => id !== draggedId,
    );
    let prevRank: string | null = null;
    let nextRank: string | null = null;
    if (beforeTaskId === null) {
        prevRank = siblings.length > 0 ? siblings[siblings.length - 1]![1].rank : null;
        nextRank = null;
    } else {
        const idx = siblings.findIndex(([id]) => id === beforeTaskId);
        if (idx === -1) return;
        prevRank = idx > 0 ? siblings[idx - 1]![1].rank : null;
        nextRank = siblings[idx]![1].rank;
    }

    // Drop in source's current slot → no-op.
    if (cur.quadrant === targetQuadrant) {
        const above = prevRank === null || cur.rank > prevRank;
        const below = nextRank === null || cur.rank < nextRank;
        if (above && below) return;
    }

    const newRank = generateKeyBetween(prevRank, nextRank);
    localApply({
        type: "move",
        id: draggedId,
        quadrant: targetQuadrant,
        rank: newRank,
    });
}

const QUADRANTS: Array<{
    q: Quadrant;
    label: string;
    verb: string;
}> = [
    {q: "q1", label: "Now", verb: "Do first"},
    {q: "q2", label: "Strategic", verb: "Schedule"},
    {q: "q3", label: "Interruptions", verb: "Delegate & batch"},
    {q: "q4", label: "Distractions", verb: "Defer or drop"},
];

export function renderBoard(root: HTMLElement, _boardId: string): void {
    root.classList.remove("landing", "eyes-paper-bg");
    root.innerHTML = "";
    root.classList.add("board");

    root.append(renderToolbar());
    root.append(renderFilterBar());
    const matrix = renderMatrix();
    root.append(matrix);
    mountCursorLayer(matrix);
    updateCursors();
    // The "newer version — reload" toast is a web affordance: reloading the
    // browser fetches the new bundle. Inside the apps the bundle is baked into
    // the binary (reloading changes nothing) and updates install silently via
    // the Tauri updater, so suppress it — it would otherwise misfire whenever
    // the app's build id differs from the server's.
    if (local.outOfDate && !isTauri()) root.append(renderUpdateToast());
}

function renderUpdateToast(): HTMLElement {
    const toast = document.createElement("div");
    toast.className = "toast toast--update";
    toast.setAttribute("role", "status");

    const msg = document.createElement("span");
    msg.textContent = "A newer version of Eyes is available.";

    const reload = document.createElement("button");
    reload.type = "button";
    reload.className = "btn btn--primary";
    reload.textContent = "Reload";
    reload.addEventListener("click", () => window.location.reload());

    toast.append(msg, reload);
    return toast;
}

function renderToolbar(): HTMLElement {
    const bar = document.createElement("header");
    bar.className = "toolbar";

    const dot = document.createElement("span");
    dot.className = `status-dot status-dot--${local.connection}`;
    dot.setAttribute("role", "status");
    dot.setAttribute("aria-label", labelForConnection(local.connection));
    dot.title = labelForConnection(local.connection);

    bar.append(dot, renderTitleInput());

    const exportBtn = iconButton(
        ICON_COPY,
        "Export board as Markdown",
        "btn btn--icon btn--toast-host",
    );
    exportBtn.addEventListener("click", async () => {
        const ok = await copyMarkdownToClipboard();
        showCopyToast(exportBtn, ok ? "Copied!" : "Copy failed", !ok);
    });

    const share = iconButton(
        ICON_LINK,
        "Copy this board's URL so you can share it",
        "btn btn--icon btn--toast-host",
    );
    share.addEventListener("click", () => {
        void copyShareLink(share);
    });

    // MCP endpoint copy button — for wiring this board into an AI
    // agent's `.mcp.json`. Hidden on phones (no plausible AI-agent
    // workflow there) via the .btn--mcp CSS rule.
    const mcp = iconButton(
        ICON_AI,
        "Copy this board's MCP endpoint URL (for AI agents)",
        "btn btn--icon btn--toast-host btn--mcp",
    );
    mcp.addEventListener("click", () => {
        void copyMcpUrl(mcp);
    });

    const search = iconButton(
        ICON_SEARCH,
        "Search & filter (press / to open)",
        "btn btn--icon",
    );
    search.addEventListener("click", () => toggleFilterBar());

    const settings = iconButton(ICON_GEAR, "Settings", "btn btn--icon");
    settings.addEventListener("click", () => openSettingsModal());

    // Desktop/mobile apps have no URL bar, so they get a "boards" button that
    // opens a dropdown of recent boards + "New board" + open-by-id. The web
    // relies on its address bar instead. See render/boards.ts.
    const boards = isTauri() ? renderBoardsButton() : null;

    bar.append(
        renderRoster(),
        mcp,
        share,
        exportBtn,
        search,
        // The manual light/dark toggle is web-only; the apps follow the OS.
        ...(isTauri() ? [] : [renderThemeToggle()]),
        ...(boards ? [boards] : []),
        settings,
    );
    return bar;
}

/** Toolbar ☀/☾ button. Shows the glyph for the theme you'd switch *to* and
 *  flips `data-theme` on <html> (persisted in localStorage by theme.ts). */
function renderThemeToggle(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn btn--icon";

    const sync = () => {
        const goingDark = effectiveTheme() === "light";
        const label = goingDark ? "Switch to dark theme" : "Switch to light theme";
        btn.title = label;
        btn.setAttribute("aria-label", label);
        // In light mode offer the moon (→ dark); in dark mode offer the sun.
        btn.replaceChildren(svgIcon(goingDark ? ICON_MOON : ICON_SUN));
    };
    sync();

    btn.addEventListener("click", () => {
        toggleTheme();
        sync();
    });
    return btn;
}

/** Toolbar board-switcher button: the board glyph plus a caret to signal the
 *  dropdown it opens (recent boards + "New board" + open-by-id). */
function renderBoardsButton(): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn btn--icon btn--boards";
    btn.title = "Switch board";
    btn.setAttribute("aria-label", "Switch board");
    btn.setAttribute("aria-haspopup", "menu");
    btn.append(svgIcon(ICON_BOARDS), svgIcon(ICON_CARET, 12));
    btn.addEventListener("click", () => openBoardsMenu(btn));
    return btn;
}

// Material-design glyphs at 24×24, filled with currentColor.
const ICON_LINK =
    "M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z";
const ICON_COPY =
    "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z";
const ICON_SEARCH =
    "M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z";
// Material `auto_awesome` (sparkles). Conventional AI affordance.
const ICON_AI =
    "M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z";
const ICON_GEAR =
    "M19.43 12.98c.04-.32.07-.64.07-.98 0-.34-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98 0 .33.03.66.07.98l-2.11 1.65c-.19.15-.24.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49-.42l.38-2.65c.61-.25 1.17-.59 1.69-.98l2.49 1c.23.09.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z";
// Material `light_mode` (sun) and `dark_mode` (crescent) for the theme toggle.
const ICON_SUN =
    "M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z";
const ICON_MOON =
    "M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9 9-4.03 9-9c0-.46-.04-.92-.1-1.36-.98 1.37-2.58 2.26-4.4 2.26-2.98 0-5.4-2.42-5.4-5.4 0-1.81.89-3.42 2.26-4.4-.44-.06-.9-.1-1.36-.1z";
// Material `grid_view` (a 2×2 grid) — the board switcher, echoing the board.
const ICON_BOARDS =
    "M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z";
// Material `arrow_drop_down` — the caret hinting the boards dropdown.
const ICON_CARET = "M7 10l5 5 5-5z";

function iconButton(
    pathD: string,
    label: string,
    className: string,
): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = className;
    btn.title = label;
    btn.setAttribute("aria-label", label);
    btn.appendChild(svgIcon(pathD));
    return btn;
}

function svgIcon(pathD: string, size = 20): SVGSVGElement {
    const ns = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(ns, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("fill", "currentColor");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS(ns, "path");
    path.setAttribute("d", pathD);
    svg.appendChild(path);
    return svg;
}

async function copyShareLink(host: HTMLButtonElement): Promise<void> {
    // Always share the canonical web URL (never a `tauri://` in-app URL) so the
    // link opens in any browser as well as the installed apps.
    const boardId = window.location.pathname.replace(/^\//, "");
    let ok = false;
    try {
        await navigator.clipboard.writeText(boardShareUrl(boardId));
        ok = true;
    } catch {
        // Fall through to the error toast.
    }
    showCopyToast(host, ok ? "Copied!" : "Couldn't copy", !ok);
}

async function copyMcpUrl(host: HTMLButtonElement): Promise<void> {
    // Path layout: /<boardId>. The MCP endpoint sits at /MCP/<boardId>
    // on the same origin (see worker/src/worker_entry.rs).
    const boardId = window.location.pathname.replace(/^\//, "");
    const url = mcpUrl(boardId);
    let ok = false;
    try {
        await navigator.clipboard.writeText(url);
        ok = true;
    } catch {
        // Fall through to the error toast.
    }
    showCopyToast(host, ok ? "MCP URL copied!" : "Couldn't copy", !ok);
}

function showCopyToast(host: HTMLElement, text: string, isError = false): void {
    // Replace any existing toast on the same host so rapid double-clicks
    // don't stack badges.
    for (const t of host.querySelectorAll(".toast--copied")) t.remove();
    const toast = document.createElement("span");
    toast.className = "toast--copied" + (isError ? " toast--copied-error" : "");
    toast.textContent = text;
    toast.setAttribute("role", "status");
    host.append(toast);
    setTimeout(() => toast.remove(), 1600);
}

function renderTitleInput(): HTMLElement {
    const input = document.createElement("input");
    input.type = "text";
    input.className = "toolbar__title";
    input.maxLength = 80;
    input.placeholder = "Eyes";
    input.spellcheck = false;
    input.setAttribute("aria-label", "Board title");
    const current = local.state.title ?? "";
    input.value = current;

    // Reflect the board title in the document title for tabs / history.
    document.title = current ? `${current} — Eyes` : "Eyes";

    let original = current;

    input.addEventListener("focus", () => {
        original = input.value;
        input.select();
    });
    input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            input.blur(); // triggers the commit below
        } else if (e.key === "Escape") {
            e.preventDefault();
            input.value = original;
            input.blur();
        }
    });
    input.addEventListener("blur", () => {
        const next = input.value.trim();
        if (next === original.trim()) return;
        localApply({type: "title", title: next.length === 0 ? null : next});
    });
    return input;
}

function labelForConnection(c: typeof local.connection): string {
    switch (c) {
        case "connecting":
            return "● Connecting";
        case "connected":
            return "● Connected";
        case "reconnecting":
            return "◐ Reconnecting";
        case "offline":
            return "○ Offline";
    }
}

function renderMatrix(): HTMLElement {
    const grid = document.createElement("div");
    grid.className = "matrix";
    grid.id = "matrix";
    grid.setAttribute("role", "grid");

    for (const meta of QUADRANTS) {
        grid.append(renderQuadrant(meta));
    }
    return grid;
}

function renderQuadrant(meta: (typeof QUADRANTS)[number]): HTMLElement {
    const cell = document.createElement("section");
    cell.className = `quadrant quadrant--${meta.q}`;
    cell.dataset.quadrant = meta.q;
    cell.setAttribute("aria-label", `${meta.label} — ${meta.verb}`);

    const header = document.createElement("header");
    header.className = "quadrant__header";
    const h2 = document.createElement("h2");
    h2.textContent = meta.label;
    const verb = document.createElement("span");
    verb.textContent = meta.verb;
    verb.className = "quadrant__verb";
    header.append(h2, verb);

    const list = document.createElement("ul");
    list.className = "quadrant__list";
    list.dataset.quadrant = meta.q;

    const showCompleted = local.state.settings.showCompleted;
    const hideBeyond = local.state.settings.hideEventsBeyondDays;
    const tasks = tasksInQuadrant(meta.q).filter(([, t]) => {
        if (!showCompleted && t.completedAt) return false;
        if (hideBeyond != null && t.dueDate) {
            const d = daysUntil(t.dueDate);
            if (d !== null && d > hideBeyond) return false;
        }
        return true;
    });
    const isEmpty = tasks.length === 0;
    // Filter bar query: keep the rows in the DOM (so the live-typing
    // path can show/hide them via class) but mark the ones that don't
    // match the query so they render as hidden.
    const q = parseQuery(local.query);
    if (isEmpty) {
        const empty = document.createElement("li");
        empty.className = "quadrant__empty";
        const emptyAdd = document.createElement("button");
        emptyAdd.type = "button";
        emptyAdd.className = "btn btn--add-empty";
        emptyAdd.textContent = "+ Add task";
        emptyAdd.addEventListener("click", () => {
            openTaskModal({mode: "add", quadrant: meta.q});
        });
        empty.append(emptyAdd);
        list.append(empty);
    } else {
        for (const [id, task] of tasks) {
            const li = renderTaskRow(id, task);
            if (!matches(task.label, q)) li.classList.add("task--filtered-out");
            list.append(li);
        }
    }

    cell.append(header, list);
    if (!isEmpty) {
        const addBtn = document.createElement("button");
        addBtn.type = "button";
        addBtn.className = "btn btn--add";
        addBtn.textContent = "+ Add task";
        addBtn.addEventListener("click", () => {
            openTaskModal({mode: "add", quadrant: meta.q});
        });
        cell.append(addBtn);
    }
    return cell;
}

function renderTaskRow(id: string, task: Task): HTMLElement {
    const li = document.createElement("li");
    const completed = !!task.completedAt;
    li.className = `task task--tint-${completed ? "none" : tintFor(task.dueDate)}`;
    if (completed) li.classList.add("task--completed");
    li.setAttribute("role", "button");
    li.tabIndex = 0;
    li.dataset.taskId = id;
    li.dataset.rank = task.rank;

    // Editing rings: collect every remote client currently editing this
    // task and apply concentric box-shadows in their colors.
    applyEditingRings(li, id);

    const num = document.createElement("span");
    num.className = "task__num";
    if (completed) {
        num.textContent = "✓";
        num.setAttribute("aria-label", "completed");
    } else {
        num.textContent = String(priorityNumber(id) ?? "?");
    }

    const label = renderLabelWithTags(task.label);
    li.append(num, label);

    // `completed` is just `!!task.completedAt`, so the date branch can
    // key off `task.completedAt` directly; the else-if implies the
    // active-task branch.
    if (task.completedAt) {
        const pill = document.createElement("span");
        pill.className = "task__pill task__pill--completed";
        pill.textContent = `done ${shortDateLabel(task.completedAt)}`;
        li.append(pill);
    } else if (task.dueDate) {
        const pill = document.createElement("span");
        pill.className = "task__pill";
        pill.textContent = relativeDateLabel(task.dueDate);
        li.append(pill);
    }

    li.append(renderCompleteToggle(id, completed));

    const openEdit = () => openTaskModal({mode: "edit", taskId: id});
    li.addEventListener("click", () => {
        // Suppress the synthetic click that fires right after a drop —
        // otherwise dropping a task would also open its edit dialog.
        if (isDragSuppressingClicks()) return;
        openEdit();
    });
    li.addEventListener("keydown", (e) => {
        // Only trigger from the row itself, not from focusable
        // descendants like hashtag buttons or the complete checkbox —
        // they activate themselves on Enter/Space.
        if (e.target !== li) return;
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openEdit();
        }
    });

    return li;
}

/** Per-task complete/uncomplete toggle. Hidden on touch (mobile uses
 *  the edit dialog) and revealed on hover for mouse users. */
function renderCompleteToggle(taskId: string, completed: boolean): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "task__check";
    const label = completed ? "Mark task active" : "Mark task complete";
    btn.title = label;
    btn.setAttribute("aria-label", label);
    btn.setAttribute("aria-pressed", String(completed));
    btn.textContent = completed ? "✓" : "";
    btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (completed) {
            // Un-completing: the row stays in place either way, so just
            // commit. Removing the strike-through is its own feedback.
            localApply({
                type: "update",
                id: taskId,
                patch: { completedAt: null },
            });
            return;
        }
        // Completing: when the row is about to disappear (the default,
        // showCompleted=false), fade it out and collapse its height
        // first so the user can see what they did and the neighbors
        // slide smoothly into the gap.
        const li = btn.closest<HTMLLIElement>("li.task");
        if (!li || local.state.settings.showCompleted) {
            localApply({
                type: "update",
                id: taskId,
                patch: { completedAt: todayLocalIsoDate() },
            });
            return;
        }
        animateCompleteThenApply(li, taskId);
    });
    // Drag-and-drop uses pointer events on the <li>; keep the checkbox
    // out of that flow so a click never starts a drag.
    btn.addEventListener("pointerdown", (e) => e.stopPropagation());
    return btn;
}

/** Lock the row's current height, kick off the `task--completing`
 *  keyframe, and commit the op once it has played (or after a fallback
 *  timeout, in case the row gets re-rendered out from under us by an
 *  inbound server message). */
function animateCompleteThenApply(li: HTMLLIElement, taskId: string): void {
    li.style.height = `${li.offsetHeight}px`;
    // Force a style flush so the explicit height takes effect before
    // the keyframe starts animating it down.
    void li.offsetHeight;
    li.classList.add("task--completing");

    let committed = false;
    const commit = () => {
        if (committed) return;
        committed = true;
        localApply({
            type: "update",
            id: taskId,
            patch: { completedAt: todayLocalIsoDate() },
        });
    };
    const FADE_MS = 260;
    const fallback = window.setTimeout(commit, FADE_MS + 40);
    li.addEventListener(
        "animationend",
        () => {
            window.clearTimeout(fallback);
            commit();
        },
        { once: true },
    );
}

/** Render a task label as a flow of text nodes + clickable `#tag`
 *  buttons. Clicking a tag appends `tag:<name>` to the filter query. */
function renderLabelWithTags(raw: string): HTMLSpanElement {
    const span = document.createElement("span");
    span.className = "task__label";
    let pos = 0;
    // matchAll returns the leading whitespace as part of the match;
    // find the actual `#` so the preceding space stays in the text run.
    TAG_RE.lastIndex = 0;
    for (const m of raw.matchAll(TAG_RE)) {
        const matchIdx = m.index!;
        const hashIdx = raw.indexOf("#", matchIdx);
        if (hashIdx > pos) span.append(raw.slice(pos, hashIdx));
        const name = m[1]!;
        span.append(renderTagButton(name));
        pos = hashIdx + 1 + name.length;
    }
    if (pos < raw.length) span.append(raw.slice(pos));
    return span;
}

function renderTagButton(name: string): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "task__tag";
    btn.textContent = `#${name}`;
    btn.setAttribute("aria-label", `Filter by tag ${name}`);
    btn.addEventListener("click", (e) => {
        e.stopPropagation();
        appendTag(name);
        // Reveal the filter bar so the user sees the active query and
        // can refine it (e.g. append free-text or another tag).
        showFilterBar();
    });
    // Don't let the click start a drag on the parent row.
    btn.addEventListener("pointerdown", (e) => e.stopPropagation());
    return btn;
}

function applyEditingRings(li: HTMLElement, taskId: string): void {
    const editors: string[] = [];
    for (const c of local.presence.values()) {
        if (c.editing === taskId && c.clientId !== local.me?.clientId) {
            editors.push(c.color);
        }
    }
    if (editors.length === 0) return;
    li.classList.add("task--being-edited");
    // Stack box-shadows concentrically per design §9.6.
    const shadow = editors
        .map((color, i) => `0 0 0 ${2 + i * 2}px ${color}`)
        .join(", ");
    li.style.boxShadow = shadow;
}

function tintFor(dueDate: string | null): "imminent" | "soon" | "none" {
    if (!dueDate) return "none";
    const days = daysUntil(dueDate);
    if (days === null) return "none";
    if (days <= local.state.settings.imminentDays) return "imminent";
    if (days <= local.state.settings.soonDays) return "soon";
    return "none";
}

function relativeDateLabel(dueDate: string): string {
    const days = daysUntil(dueDate);
    if (days === null) return dueDate;
    if (days < 0) return `overdue ${-days}d`;
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    if (days < 14) return `in ${days}d`;
    if (days <= 30) return `in ${Math.floor(days / 7)}w`;
    return shortDateLabel(dueDate);
}

/** Locale-aware "May 13"-style label for an ISO YYYY-MM-DD. */
function shortDateLabel(isoDate: string): string {
    const d = new Date(isoDate + "T00:00:00");
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString(undefined, {month: "short", day: "numeric"});
}

function daysUntil(dueDate: string): number | null {
    const due = new Date(dueDate + "T00:00:00");
    if (isNaN(due.getTime())) return null;
    const today = startOfLocalDay(new Date());
    const ms = due.getTime() - today.getTime();
    return Math.round(ms / (24 * 60 * 60 * 1000));
}

function startOfLocalDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
